import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import AuthEvents from '@/domains/auth/events';
import { AuthPageContainer } from '@/domains/auth/layouts/AuthPageContainer/AuthPageContainer';
import { forgotUsername } from '@/domains/auth/store/authSlice';
import { selectAuthForgotUsernameIsLoading } from '@/domains/auth/store/selectors';
import { FormInput } from '@/components/form/controlled/FormInput/FormInput';
import { useEventEffect } from '@/hooks/useEventEffect';
import { RootPaths } from '@/routes/paths';
import { useDispatch, useSelector } from '@/store/hooks';
import { AdaptiveButton, ForgotButton, ForgotOptions, Form, Options, Title } from './ForgotUsername.styles';
import { validationSchema } from './validationSchema';

export const FORGOT_USERNAME_ERROR_MOCK = 'Unauthorized';

const defaultValues = {
  email: '',
};

type FormValues = typeof defaultValues;

export const ForgotUsername = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const isLoading = useSelector(selectAuthForgotUsernameIsLoading);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setError,
    clearErrors,
  } = useForm<FormValues>({
    resolver: zodResolver(validationSchema),
    reValidateMode: 'onSubmit',
    defaultValues,
  });

  const email = watch('email');

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    dispatch(forgotUsername(data));
  };

  useEffect(() => {
    clearErrors('email');
  }, [email, clearErrors]);

  useEventEffect(AuthEvents.ForgotUserNameError, () =>
    setError('email', { message: FORGOT_USERNAME_ERROR_MOCK }, { shouldFocus: false }),
  );

  useEventEffect(AuthEvents.RedirectToForgotUserNameEmail, () => navigate(RootPaths.FORGOT_USERNAME_EMAIL));

  return (
    <AuthPageContainer>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Title>
          <Typography data-testid="ForgotUsernameTitle" variant={isSmallScreen ? 'h5' : 'h3'}>
            Restore Username
          </Typography>
          <Typography variant={isSmallScreen ? 'body2' : 'body1'}>Welcome Back! Please Enter Your Details.</Typography>
        </Title>
        <FormInput name="email" control={control} label="Email" errorMessage={errors.email?.message} />
        <Options>
          <AdaptiveButton
            data-testid="ForgotUsernameLoginButton"
            size="large"
            type="submit"
            width="fluid"
            disabled={isLoading}>
            LOG IN
          </AdaptiveButton>
          <ForgotOptions>
            <Link to={RootPaths.FORGOT_USERNAME}>
              <ForgotButton name="ForgotUsernameButton" variant="additionalLinkSecondary" size="small">
                Forgot Username
              </ForgotButton>
            </Link>
            <Link to={RootPaths.FORGOT_PASSWORD}>
              <ForgotButton name="ForgotUsernamePasswordButton" variant="additionalLinkSecondary" size="small">
                Forgot Password
              </ForgotButton>
            </Link>
          </ForgotOptions>
        </Options>
      </Form>
    </AuthPageContainer>
  );
};
